import React, { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  Brain, 
  Database, 
  FileText, 
  Network, 
  Globe, 
  Zap,
  Activity
} from 'lucide-react';

interface DataSource {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  position: 'top' | 'top-right' | 'right' | 'bottom-right' | 'bottom' | 'bottom-left' | 'left' | 'top-left';
  color: string;
  delay: number;
  dataPoints: string[];
}

const AIEngineVisualization: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeNode, setActiveNode] = useState<string | null>(null);
  const [showDataFlow, setShowDataFlow] = useState(false);
  
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      setIsVisible(true);
      setTimeout(() => setShowDataFlow(true), 2000);
    }
  }, [isInView]);

  const dataSources: DataSource[] = [
    {
      id: 'erp',
      name: 'ERP Systems',
      description: 'Hospital management data',
      icon: Database,
      position: 'top-left',
      color: 'bg-blue-100 text-blue-600',
      delay: 0.4,
      dataPoints: ['Patient records', 'Inventory levels', 'Order history']
    },
    {
      id: 'gpo',
      name: 'GPO Contract Databases',
      description: 'Group purchasing data',
      icon: FileText,
      position: 'top',
      color: 'bg-green-100 text-green-600',
      delay: 0.6,
      dataPoints: ['Contract terms', 'Pricing data', 'Volume discounts']
    },
    {
      id: 'mims',
      name: 'MIMS / Product Registries',
      description: 'Medical product information',
      icon: Network,
      position: 'top-right',
      color: 'bg-purple-100 text-purple-600',
      delay: 0.8,
      dataPoints: ['Product details', 'Regulatory info', 'Clinical data']
    },
    {
      id: 'edi',
      name: 'EDI Transactions',
      description: 'Electronic data interchange',
      icon: Zap,
      position: 'right',
      color: 'bg-yellow-100 text-yellow-600',
      delay: 1.0,
      dataPoints: ['Real-time orders', 'Shipment tracking', 'Payment data']
    },
    {
      id: 'distributor',
      name: 'Distributor / Supplier Portals',
      description: 'Supplier network data',
      icon: Globe,
      position: 'bottom-right',
      color: 'bg-red-100 text-red-600',
      delay: 1.2,
      dataPoints: ['Stock availability', 'Lead times', 'Supplier ratings']
    },
    {
      id: 'clinical',
      name: 'Clinical Decision Support',
      description: 'Medical knowledge base',
      icon: Brain,
      position: 'bottom',
      color: 'bg-indigo-100 text-indigo-600',
      delay: 1.4,
      dataPoints: ['Clinical guidelines', 'Drug interactions', 'Best practices']
    },
    {
      id: 'analytics',
      name: 'Analytics & Reporting',
      description: 'Performance insights',
      icon: Activity,
      position: 'bottom-left',
      color: 'bg-orange-100 text-orange-600',
      delay: 1.6,
      dataPoints: ['Usage patterns', 'Cost analysis', 'Performance metrics']
    },
    {
      id: 'integration',
      name: 'Integration Hub',
      description: 'System connectivity',
      icon: Activity,
      position: 'left',
      color: 'bg-teal-100 text-teal-600',
      delay: 1.8,
      dataPoints: ['API connections', 'Data mapping', 'Security protocols']
    }
  ];

  const getPositionClasses = (position: string) => {
    const positions = {
      'top': 'top-8 left-[47%] transform -translate-x-1/2',
      'top-right': 'top-8 right-8',
      'top-left': 'top-8 left-8',
      'right': 'top-1/2 right-[10%] transform -translate-y-1/2',
      'bottom-right': 'bottom-8 right-8',
      'bottom': 'bottom-8 left-1/2 transform -translate-x-1/2',
      'bottom-left': 'bottom-8 left-8',
      'left': 'top-1/2 left-8 transform -translate-y-1/2'
    };
    return positions[position as keyof typeof positions] || '';
  };

  const getConnectionPath = (position: string) => {
    const paths = {
      'top': 'M 200 200 L 200 120',
      'top-right': 'M 200 200 L 280 120',
      'top-left': 'M 200 200 L 120 120',
      'right': 'M 200 200 L 280 200',
      'bottom-right': 'M 200 200 L 280 280',
      'bottom': 'M 200 200 L 200 280',
      'bottom-left': 'M 200 200 L 120 280',
      'left': 'M 200 200 L 120 200'
    };
    return paths[position as keyof typeof paths] || '';
  };

  const getParticleStartPosition = (position: string) => {
    const positions = {
      'top': { x: 200, y: 120 },
      'top-right': { x: 280, y: 120 },
      'top-left': { x: 120, y: 120 },
      'right': { x: 280, y: 200 },
      'bottom-right': { x: 280, y: 280 },
      'bottom': { x: 200, y: 280 },
      'bottom-left': { x: 120, y: 280 },
      'left': { x: 120, y: 200 }
    };
    return positions[position as keyof typeof positions] || { x: 200, y: 200 };
  };

  return (
    <section className="py-16 sm:py-20 lg:py-32 bg-gradient-to-br from-neutral-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-4 sm:mb-6 lg:mb-8"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-neutral-900 mb-4 sm:mb-6">
            Proprietary AI Engine
          </h2>
          <p className="text-lg sm:text-xl text-neutral-600 max-w-3xl mx-auto px-4">
            Our intelligent platform seamlessly bridges the gap between hospitals and suppliers to address real-time disruptions
          </p>
        </motion.div>

        {/* Interactive Visualization */}
        <div className="relative w-full max-w-5xl mx-auto">
          <div 
            ref={ref}
            className="relative w-full aspect-square max-w-3xl mx-auto"
          >
            {/* Connection Lines */}
            <svg 
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 400 400"
            >
              {dataSources.map((source) => (
                <motion.path
                  key={`connection-${source.id}`}
                  d={getConnectionPath(source.position)}
                  stroke="url(#gradient)"
                  strokeWidth="2"
                  fill="none"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={isVisible ? { pathLength: 1, opacity: 0.3 } : {}}
                  transition={{ 
                    duration: 1.5, 
                    delay: source.delay + 0.5,
                    ease: "easeInOut"
                  }}
                  className="drop-shadow-sm"
                />
              ))}
              
              {/* Data Flow Particles */}
              {showDataFlow && dataSources.map((source, sourceIndex) => (
                // Create multiple particles for each data source
                Array.from({ length: 3 }).map((_, particleIndex) => (
                  <motion.circle
                    key={`particle-${source.id}-${particleIndex}`}
                    r="4"
                    fill="url(#particleGradient)"
                    initial={{ 
                      cx: getParticleStartPosition(source.position).x,
                      cy: getParticleStartPosition(source.position).y,
                      opacity: 0
                    }}
                    animate={{ 
                      cx: 200,
                      cy: 200,
                      opacity: [0, 1, 0]
                    }}
                    transition={{ 
                      duration: 2.5,
                      delay: sourceIndex * 0.5 + particleIndex * 0.8,
                      repeat: Infinity,
                      repeatDelay: 2,
                      ease: "easeInOut"
                    }}
                  />
                ))
              ))}
              
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#b1a1ff" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.6" />
                </linearGradient>
                <linearGradient id="particleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#b1a1ff" />
                  <stop offset="100%" stopColor="#0ea5e9" />
                </linearGradient>
              </defs>
            </svg>

            {/* Center AI Engine */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={isVisible ? { scale: 1, opacity: 1 } : {}}
              transition={{ 
                duration: 0.8, 
                delay: 0.3,
                type: "spring",
                stiffness: 200
              }}
              className="absolute z-20"
              style={{
                left: '45%',
                top: '45%',
                transform: 'translate(-50%, -50%)'
              }}
            >
              <motion.div
                animate={{ 
                  scale: [1, 1.05, 1],
                  boxShadow: [
                    "0 0 0 0 rgba(177, 161, 255, 0.4)",
                    "0 0 0 20px rgba(177, 161, 255, 0)",
                    "0 0 0 0 rgba(177, 161, 255, 0)"
                  ]
                }}
                transition={{ 
                  duration: 3, 
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="relative"
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-full flex items-center justify-center shadow-2xl">
                  <Brain size={24} className="sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-white" />
                </div>
                
                {/* Pulsing ring */}
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.6, 0, 0.6]
                  }}
                  transition={{ 
                    duration: 2, 
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="absolute inset-0 border-2 border-primary-400 rounded-full"
                />
                
                {/* Processing indicator */}
                {showDataFlow && (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0 border-2 border-transparent border-t-primary-300 rounded-full"
                  />
                )}
              </motion.div>
              
              {/* Center label */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 1.2 }}
                className="absolute text-center"
                style={{
                  width: 'max-content',
                  left: '-15%',
                  top: '100%',
                  transform: 'translateX(-50%)',
                  marginTop: '1rem'
                }}
              >
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-neutral-900 mb-1">
                  MedAlt AI Engine
                </h3>
                <p className="text-xs sm:text-sm text-neutral-600">
                  Intelligent Processing
                </p>
                {showDataFlow && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center justify-center gap-1 mt-1"
                  >
                    <Activity size={12} className="text-primary-500" />
                    <span className="text-xs text-primary-600 font-medium">Processing</span>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>

            {/* Intelligent Workflow Statement */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isVisible ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 1.0 }}
              className="absolute z-15 text-center"
              style={{
                left: '35%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                marginTop: '8rem'
              }}
            >
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-3 shadow-lg border border-primary-200">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-primary-600">
                  Intelligent Workflow
                </h2>
              </div>
            </motion.div>

            {/* Data Source Nodes */}
            {dataSources.map((source) => (
              <motion.div
                key={source.id}
                initial={{ scale: 0, opacity: 0 }}
                animate={isVisible ? { scale: 1, opacity: 1 } : {}}
                transition={{ 
                  duration: 0.6, 
                  delay: source.delay,
                  type: "spring",
                  stiffness: 200
                }}
                className={`absolute ${getPositionClasses(source.position)} z-10`}
                onMouseEnter={() => setActiveNode(source.id)}
                onMouseLeave={() => setActiveNode(null)}
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 ${source.color} rounded-full flex items-center justify-center shadow-lg transition-all duration-300 cursor-pointer ${
                    activeNode === source.id ? 'ring-4 ring-primary-300 ring-opacity-50' : ''
                  }`}
                >
                  <source.icon size={24} className="sm:w-6 sm:h-6 lg:w-8 lg:h-8" />
                </motion.div>
                
                {/* Node label */}
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={isVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: source.delay + 0.3 }}
                  className="absolute top-full left-1/2 transform -translate-x-1/2 mt-3 text-center min-w-max"
                  style={{ width: 'max-content', left: '50%', transform: 'translateX(-50%)' }}
                >
                  <h4 className="text-sm sm:text-base font-semibold text-neutral-900 mb-1 text-center">
                    {source.name}
                  </h4>
                  <p className="text-xs sm:text-sm text-neutral-600 max-w-32 sm:max-w-40 text-center">
                    {source.description}
                  </p>
                  
                  {/* Data points on hover */}
                  {activeNode === source.id && (
                    <motion.div
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 p-2 bg-white rounded-lg shadow-lg border border-neutral-200"
                    >
                      <div className="text-xs font-medium text-neutral-700 mb-1">Data Points:</div>
                      <div className="space-y-1">
                        {source.dataPoints.map((point, index) => (
                          <div key={index} className="text-xs text-neutral-600 flex items-center gap-1">
                            <div className="w-1 h-1 bg-primary-400 rounded-full"></div>
                            {point}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export { AIEngineVisualization };